// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 MindTenet LLC
// This file is part of Undef Engine.

/**
 * Animated Number Component
 * Smoothly animates number changes with optional formatting
 */

import { useEffect, useRef, useState } from 'react';
import { motion, useSpring, useTransform } from 'framer-motion';

interface AnimatedNumberProps {
  value: number;
  /** Duration of animation in seconds */
  duration?: number;
  /** Format function for display */
  format?: (n: number) => string;
  /** CSS class for the container */
  className?: string;
  /** Show + prefix for positive changes */
  showSign?: boolean;
  /** Highlight color on increase */
  highlightOnIncrease?: boolean;
  /** Scale animation on change */
  scaleOnChange?: boolean;
}

/**
 * Animated number that smoothly transitions between values
 */
export function AnimatedNumber({
  value,
  duration = 0.5,
  format = n => Math.round(n).toLocaleString(),
  className = '',
  showSign = false,
  highlightOnIncrease = false,
  scaleOnChange = true,
}: AnimatedNumberProps) {
  const previousValue = useRef(value);
  const [isIncreasing, setIsIncreasing] = useState(false);

  const spring = useSpring(value, {
    stiffness: 100,
    damping: 20,
    duration: duration * 1000,
  });

  const display = useTransform(spring, current => {
    const formatted = format(current);
    if (showSign && value > previousValue.current) {
      return `+${formatted}`;
    }
    return formatted;
  });

  useEffect(() => {
    if (value !== previousValue.current) {
      setIsIncreasing(value > previousValue.current);
      spring.set(value);

      // Reset highlight after animation
      const timeout = setTimeout(() => {
        setIsIncreasing(false);
      }, duration * 1000);

      previousValue.current = value;
      return () => clearTimeout(timeout);
    }
  }, [value, duration, spring]);

  return (
    <motion.span
      className={`
        inline-block tabular-nums
        ${highlightOnIncrease && isIncreasing ? 'text-green-400' : ''}
        ${className}
      `}
      animate={scaleOnChange && isIncreasing ? { scale: [1, 1.2, 1] } : {}}
      transition={{ duration: 0.3 }}
    >
      <motion.span>{display}</motion.span>
    </motion.span>
  );
}

/**
 * Simple animated counter that counts up from 0
 */
export function CountUp({
  to,
  duration = 1,
  delay = 0,
  className = '',
}: {
  to: number;
  duration?: number;
  delay?: number;
  className?: string;
}) {
  const [count, setCount] = useState(0);

  useEffect(() => {
    const timeout = setTimeout(() => {
      const startTime = Date.now();
      const endTime = startTime + duration * 1000;

      const animate = () => {
        const now = Date.now();
        const progress = Math.min((now - startTime) / (duration * 1000), 1);
        // Ease out cubic
        const eased = 1 - Math.pow(1 - progress, 3);
        setCount(Math.round(eased * to));

        if (now < endTime) {
          requestAnimationFrame(animate);
        }
      };

      requestAnimationFrame(animate);
    }, delay * 1000);

    return () => clearTimeout(timeout);
  }, [to, duration, delay]);

  return <span className={`tabular-nums ${className}`}>{count.toLocaleString()}</span>;
}

/**
 * Score display that pops and highlights on change
 */
export function AnimatedScore({
  score,
  label,
  className = '',
}: {
  score: number;
  label?: string;
  className?: string;
}) {
  return (
    <div className={`flex flex-col items-center ${className}`}>
      {label && <span className="text-xs text-gray-400 uppercase tracking-wide">{label}</span>}
      <AnimatedNumber
        value={score}
        className="text-2xl font-bold"
        highlightOnIncrease
        scaleOnChange
      />
    </div>
  );
}
